import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Portal-based tooltip used by the collapsed sidebar across every
 * layout (Admin, User, SuperAdmin). Renders the label as a small
 * floating chip to the right of the anchor row.
 *
 * Why a portal: every sidebar's `<nav>` has `overflow-y-auto` (which
 * browsers compute as `overflow-x: auto` too). A `position: absolute`
 * tooltip inside the row would get clipped at the 68px-wide rail's
 * right edge. Portaling to `document.body` + `position: fixed` makes
 * the tooltip's coordinates viewport-relative — independent of any
 * ancestor overflow context — so it can extend freely into the
 * main content area.
 *
 * Show/hide is wired to native `mouseenter` / `mouseleave` on the
 * anchor (plus `focusin` / `focusout` for keyboard a11y) instead of a
 * CSS `:hover` rule, because the visible tooltip lives in a different
 * DOM subtree and CSS hover doesn't propagate across portals.
 *
 * TOUCH: both triggers misfire on a phone. Mobile browsers synthesise
 * `mouseenter` from a tap, and tapping a NavLink also focuses it — and
 * that focus SURVIVES the navigation, with no `mouseleave` ever
 * arriving to clear it. The result was a tooltip that appeared on tap
 * and then sat over the page content indefinitely (QA, 2026-09-01).
 * So pointer triggers are gated on a hover-capable, fine pointer, and
 * the focus trigger on `:focus-visible` — which a tap does not set,
 * but keyboard navigation does. Mouse and keyboard behaviour is
 * unchanged; touch simply never shows a tooltip, which is correct:
 * there is no hover state to explain an icon on a touchscreen.
 *
 * Usage:
 *   const ref = useRef(null)
 *   return (
 *     <div ref={ref}>
 *       <NavLink ... />
 *       {collapsed ? <CollapsedNavTooltip label="Dashboard" anchorRef={ref} /> : null}
 *     </div>
 *   )
 *
 * Or use the `<TooltipWrap>` helper below which encapsulates the ref +
 * the wrapping div + the conditional render in one component.
 */
export function CollapsedNavTooltip({ label, anchorRef }) {
  const [coords, setCoords] = useState(null)
  useEffect(() => {
    const el = anchorRef.current
    if (!el) return undefined
    function show() {
      const rect = el.getBoundingClientRect()
      setCoords({ top: rect.top + rect.height / 2, left: rect.right + 12 })
    }
    function hide() {
      setCoords(null)
    }
    /** True only on a device with a real hover state and a precise
     *  pointer — a mouse or trackpad. False on touch, where the browser
     *  fakes `mouseenter` from a tap. Read at event time, not once on
     *  mount, so a hybrid laptop that switches input mode is handled. */
    function pointerCanHover() {
      return window.matchMedia?.('(hover: hover) and (pointer: fine)').matches ?? false
    }
    function showFromPointer() {
      if (pointerCanHover()) show()
    }
    /** Keyboard focus only. A tap focuses the link but does NOT set
     *  `:focus-visible`, so this stays silent on touch while a Tab press
     *  still surfaces the label for keyboard users. */
    function showFromFocus(event) {
      try {
        if (event.target?.matches?.(':focus-visible')) show()
      } catch {
        // :focus-visible unsupported (older Safari) — fall back to the
        // pointer capability check rather than showing on every tap.
        if (pointerCanHover()) show()
      }
    }
    el.addEventListener('mouseenter', showFromPointer)
    el.addEventListener('mouseleave', hide)
    el.addEventListener('focusin', showFromFocus)
    el.addEventListener('focusout', hide)
    return () => {
      el.removeEventListener('mouseenter', showFromPointer)
      el.removeEventListener('mouseleave', hide)
      el.removeEventListener('focusin', showFromFocus)
      el.removeEventListener('focusout', hide)
    }
  }, [anchorRef])
  if (!coords) return null
  return createPortal(
    <div
      role="tooltip"
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        transform: 'translateY(-50%)',
      }}
      // Inverted chip: `--color-ink-strong` background pairs with
      // `--color-canvas` text so BOTH flip with the theme — dark navy
      // chip / light text in light mode, near-white chip / dark text in
      // dark mode. A hardcoded `text-white` here was invisible in dark
      // mode (white text on the near-white ink-strong background).
      className="z-[100] px-2.5 py-1.5 rounded-md bg-[color:var(--color-ink-strong)] text-[color:var(--color-canvas)] text-xs font-medium whitespace-nowrap pointer-events-none shadow-lg"
    >
      {label}
    </div>,
    document.body,
  )
}

/**
 * Convenience wrapper: encapsulates the ref + the anchor div + the
 * conditional render of `CollapsedNavTooltip`. Drop a single NavLink
 * (or any focusable element) inside and pass the label + a
 * `collapsed` flag — the tooltip turns on automatically when the
 * sidebar is in icon-only mode.
 */
export function CollapsedTooltipWrap({ label, collapsed, children }) {
  const ref = useRef(null)
  return (
    <div ref={ref}>
      {children}
      {collapsed ? <CollapsedNavTooltip label={label} anchorRef={ref} /> : null}
    </div>
  )
}
